'use client';

import React, { useState, useCallback } from 'react';
import { Bars3Icon, ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { cn } from '@/lib/utils';

interface SortableListProps<T extends { id: string }> {
  items: T[];
  onReorder: (items: T[]) => void;
  renderItem: (item: T, index: number) => React.ReactNode;
  className?: string;
  itemClassName?: string;
  disabled?: boolean;
  dragHandleClassName?: string;
  /**
   * Opt in to a Move up / Move down button pair per row — the keyboard and touch path for a drag.
   * Each returns the accessible name for that row's button (include the item's name).
   */
  moveLabels?: { up: (item: T) => string; down: (item: T) => string };
}

export function SortableList<T extends { id: string }>({
  items,
  onReorder,
  renderItem,
  className,
  itemClassName,
  disabled = false,
  dragHandleClassName,
  moveLabels,
}: SortableListProps<T>) {
  const [draggedItem, setDraggedItem] = useState<T | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const handleDragStart = useCallback(
    (e: React.DragEvent, item: T) => {
      if (disabled) return;
      setDraggedItem(item);
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', item.id);
    },
    [disabled],
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent, index: number) => {
      if (disabled || !draggedItem) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';

      const draggedIndex = items.findIndex((item) => item.id === draggedItem.id);
      if (draggedIndex === index) return;

      // Get the bounding rect of the target element
      const rect = e.currentTarget.getBoundingClientRect();
      const midY = rect.top + rect.height / 2;

      // If mouse is in the upper half, drop before the item
      // If mouse is in the lower half, drop after the item
      const dropIndex = e.clientY < midY ? index : index + 1;

      setDragOverIndex(dropIndex);
    },
    [disabled, draggedItem, items],
  );

  const handleDragLeave = useCallback(() => {
    setDragOverIndex(null);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent, dropIndex: number) => {
      if (disabled || !draggedItem) return;
      e.preventDefault();

      const draggedIndex = items.findIndex((item) => item.id === draggedItem.id);
      if (draggedIndex === -1) {
        setDraggedItem(null);
        setDragOverIndex(null);
        return;
      }

      // If dropping on the same position, do nothing
      if (draggedIndex === dropIndex) {
        setDraggedItem(null);
        setDragOverIndex(null);
        return;
      }

      // Create new array with reordered items
      const newItems = [...items];
      const [removed] = newItems.splice(draggedIndex, 1);
      newItems.splice(dropIndex, 0, removed);

      // Update sort_order for all items
      const updatedItems = newItems.map((item, index) => ({
        ...item,
        sort_order: index,
      }));

      onReorder(updatedItems);
      setDraggedItem(null);
      setDragOverIndex(null);
    },
    [disabled, draggedItem, items, onReorder],
  );

  // Same output as a drop: the moved item's new position, every row renumbered.
  const handleMove = useCallback(
    (index: number, delta: -1 | 1) => {
      const target = index + delta;
      if (disabled || target < 0 || target >= items.length) return;
      const newItems = [...items];
      const [moved] = newItems.splice(index, 1);
      newItems.splice(target, 0, moved);
      onReorder(newItems.map((item, i) => ({ ...item, sort_order: i })));
    },
    [disabled, items, onReorder],
  );

  const handleDragEnd = useCallback(() => {
    setDraggedItem(null);
    setDragOverIndex(null);
  }, []);

  if (disabled) {
    return (
      <div className={cn('space-y-2', className)}>
        {items.map((item, index) => (
          <div key={item.id} className={cn('', itemClassName)}>
            {renderItem(item, index)}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={cn('space-y-2', className)}>
      {/* Drop zone at the beginning */}
      <div
        className={cn('min-h-[4px] rounded transition-all duration-200', {
          'border-t-2 border-blue-400 bg-blue-50': dragOverIndex === 0,
        })}
        onDragOver={(e) => {
          if (disabled || !draggedItem) return;
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
          setDragOverIndex(0);
        }}
        onDragLeave={handleDragLeave}
        onDrop={(e) => handleDrop(e, 0)}
      />

      {items.map((item, index) => (
        <div
          key={item.id}
          className={cn(
            'transition-all duration-200',
            {
              'opacity-50': draggedItem?.id === item.id,
              'border-t-2 border-blue-400 bg-blue-50':
                dragOverIndex === index && draggedItem?.id !== item.id,
              'border-b-2 border-blue-400 bg-blue-50':
                dragOverIndex === index + 1 && draggedItem?.id !== item.id,
            },
            itemClassName,
          )}
          onDragOver={(e) => handleDragOver(e, index)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, index)}
          onDragEnd={handleDragEnd}
        >
          <div draggable onDragStart={(e) => handleDragStart(e, item)} className="cursor-move">
            <div className="flex items-center gap-2">
              <div className={cn('text-foreground hover:text-foreground', dragHandleClassName)}>
                <Bars3Icon className="h-4 w-4" />
              </div>
              <div className="flex-1">{renderItem(item, index)}</div>
              {moveLabels && (
                <div className="flex shrink-0 items-center gap-1">
                  {(
                    [
                      { delta: -1, label: moveLabels.up(item), Icon: ChevronUpIcon },
                      { delta: 1, label: moveLabels.down(item), Icon: ChevronDownIcon },
                    ] as const
                  ).map(({ delta, label, Icon }) => (
                    <button
                      key={delta}
                      type="button"
                      aria-label={label}
                      title={label}
                      disabled={delta < 0 ? index === 0 : index === items.length - 1}
                      onClick={() => handleMove(index, delta)}
                      className="text-foreground hover:bg-surface focus-visible:ring-primary rounded p-1.5 focus-visible:ring-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      <Icon className="h-4 w-4" aria-hidden="true" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      ))}

      {/* Drop zone at the end */}
      <div
        className={cn('min-h-[4px] rounded transition-all duration-200', {
          'border-t-2 border-blue-400 bg-blue-50': dragOverIndex === items.length,
        })}
        onDragOver={(e) => {
          if (disabled || !draggedItem) return;
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
          setDragOverIndex(items.length);
        }}
        onDragLeave={handleDragLeave}
        onDrop={(e) => handleDrop(e, items.length)}
      />
    </div>
  );
}
